import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { LOCALE_LABELS, normalizeLocale, visibleLocales, type AppLocale } from '../shared/i18n';
import { useTheme } from '../app/ThemeProvider';

/**
 * The application header.
 *
 * The search box is present on every screen because it is the product: a user
 * who has landed on a company page from Google must be able to start another
 * check without navigating home first. On a narrow screen it collapses to an
 * icon link, which is why the label is on the link rather than on the input.
 */
export function Header({ showSearch = true }: { showSearch?: boolean }): React.ReactNode {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  const current = normalizeLocale(i18n.language) ?? 'es';
  const languages = visibleLocales(i18n.language);

  return (
    <header className="sticky top-0 z-30 border-b border-[var(--color-border)] bg-[var(--color-surface)]/95 backdrop-blur">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-brand-700 focus:px-3 focus:py-2 focus:text-white"
      >
        {t('header.skipToContent')}
      </a>

      <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-3 sm:h-16 sm:px-4">
        <Link
          to="/"
          aria-label={t('header.home')}
          className="shrink-0 text-lg font-extrabold tracking-tight text-[var(--color-fg)] sm:text-xl"
        >
          FOCO
        </Link>

        {showSearch ? (
          <form
            role="search"
            className="relative hidden min-w-0 flex-1 sm:block"
            onSubmit={(event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              const query = String(data.get('q') ?? '').trim();
              if (query) navigate(`/search?q=${encodeURIComponent(query)}`);
            }}
          >
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-fg)]"
            />
            <input
              name="q"
              type="search"
              aria-label={t('header.searchLabel')}
              placeholder={t('header.searchPlaceholder')}
              className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface-2)] pl-9 pr-3 text-sm text-[var(--color-fg)] outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/30"
            />
          </form>
        ) : (
          <div className="flex-1" />
        )}

        <Link
          to="/search"
          aria-label={t('header.searchLabel')}
          className="rounded-[var(--radius-control)] p-2 text-[var(--color-fg)] hover:bg-[var(--color-surface-2)] sm:hidden"
        >
          <Search aria-hidden className="h-5 w-5" />
        </Link>

        <nav className="flex shrink-0 items-center gap-1 text-sm">
          <Link
            to="/pricing"
            className="hidden rounded-[var(--radius-control)] px-3 py-2 text-[var(--color-muted-fg)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-fg)] sm:block"
          >
            {t('header.pricing')}
          </Link>

          {/* Language switcher: Russian appears only when the browser itself is
              Russian, so a Brazilian user is not led into a half-empty locale. */}
          <label className="sr-only" htmlFor="locale-switch">
            {t('common.language')}
          </label>
          <select
            id="locale-switch"
            value={current}
            onChange={(event) => void i18n.changeLanguage(event.target.value)}
            className="h-9 rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface-2)] px-2 text-sm text-[var(--color-fg)] outline-none focus:border-brand-600"
          >
            {languages.map((locale: AppLocale) => (
              <option key={locale} value={locale}>
                {LOCALE_LABELS[locale]}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'system')}
            aria-label={t('common.theme')}
            title={`${t('common.theme')}: ${t(`theme.${theme}`)}`}
            className="rounded-[var(--radius-control)] px-2 py-2 text-[var(--color-muted-fg)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-fg)]"
          >
            {theme === 'dark' ? '🌙' : theme === 'light' ? '☀️' : '🖥️'}
          </button>

          <Link
            to="/signin"
            className="rounded-[var(--radius-control)] px-3 py-2 text-[var(--color-muted-fg)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-fg)]"
          >
            {t('header.signIn')}
          </Link>
        </nav>
      </div>
    </header>
  );
}
